import { ChevronDown, ChevronUp, Lightbulb, Trash2 } from "lucide-react";
import { useCallback } from "react";

import type { UseBuilderDraft } from "@/hooks/useBuilderDraft";
import { useT } from "@/i18n";
import type { DisplayLabel, LocalizedString } from "@pervasivemind/dashboard-render";
import { BilingualInput } from "@/pages/builder/components/BilingualInput";
import { ChipsInput, Field } from "@/pages/builder/components/Field";
import type { InsightSpec } from "@/types";

interface InsightSpecsEditorProps {
  draft: UseBuilderDraft;
}

/**
 * Editor for ``ai_config.insight_specs`` — the per-insight
 * configuration the snapshot service iterates over to produce one
 * insight per spec (replacing the legacy "free-form 3 insights"
 * generation).
 *
 * Each spec carries:
 *
 * - ``id``: stable slug used to match insights across snapshots.
 *   Auto-suggested from the title slug on first focus.
 * - ``title`` (bilingual): rendered verbatim by the portal as the
 *   insight card header. The LLM does NOT rewrite this.
 * - ``question`` (bilingual): plain-language instruction the LLM
 *   answers in ``summary`` + ``detail``.
 * - ``data_focus``: optional whitelist of KPI keys / chart titles
 *   the LLM should consider for this insight.
 * - ``priority``: lower runs first.
 * - ``enabled``: toggle without deleting.
 *
 * Empty state = legacy behavior (LLM free-forms 3 insights).
 */
export function InsightSpecsEditor({
  draft,
}: InsightSpecsEditorProps): JSX.Element {
  const { t } = useT();
  const specs = draft.ai.insight_specs ?? [];

  const update = useCallback(
    (next: InsightSpec[]): void => {
      draft.setAI({ insight_specs: next });
    },
    [draft],
  );

  const add = useCallback((): void => {
    // Seed with the highest priority + 1 so a newly-added spec lands
    // at the bottom of the order; the admin can move it up via the
    // arrow buttons.
    const maxPriority = specs.reduce(
      (acc, s) => Math.max(acc, s.priority ?? 0),
      0,
    );
    update([
      ...specs,
      {
        id: "",
        enabled: true,
        title: { en: "", es: "" },
        question: { en: "", es: "" },
        data_focus: [],
        priority: maxPriority + 1,
      },
    ]);
  }, [specs, update]);

  const patch = useCallback(
    (idx: number, partial: Partial<InsightSpec>): void => {
      update(specs.map((s, i) => (i === idx ? { ...s, ...partial } : s)));
    },
    [specs, update],
  );

  const remove = useCallback(
    (idx: number): void => {
      update(specs.filter((_, i) => i !== idx));
    },
    [specs, update],
  );

  const move = useCallback(
    (idx: number, delta: -1 | 1): void => {
      const target = idx + delta;
      if (target < 0 || target >= specs.length) return;
      const next = [...specs];
      const [item] = next.splice(idx, 1);
      next.splice(target, 0, item!);
      // Re-number priority so the visual order matches the persisted
      // ``priority`` — keeps the server-side sort stable when other
      // tooling reads the JSON later.
      update(next.map((s, i) => ({ ...s, priority: i + 1 })));
    },
    [specs, update],
  );

  /** Slugify a title for the auto-id suggestion. */
  const slugify = (label: DisplayLabel): string => {
    const raw =
      typeof label === "string"
        ? label
        : label && typeof label === "object"
          ? (label.en ?? label.es ?? "")
          : "";
    return raw
      .toLowerCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "") // strip accents
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 40);
  };

  return (
    <section className="rounded-[14px] border border-neutral-200 bg-white shadow-card p-5">
      <header className="mb-4 flex items-center gap-2">
        <Lightbulb className="h-4 w-4 text-primary-dark" />
        <h2 className="text-base font-semibold text-neutral-900">
          {t("editor.ai.insights.specsTitle")}
        </h2>
      </header>
      <p className="mb-4 text-xs text-neutral-500">
        {t("editor.ai.insights.specsDescription")}
      </p>

      <div className="space-y-3">
        {specs.length === 0 && (
          <p className="rounded-md border border-dashed border-neutral-200 bg-neutral-50 p-4 text-sm italic text-neutral-500">
            {t("editor.ai.insights.specsEmpty")}
          </p>
        )}
        {specs.map((spec, idx) => {
          const titleSuggestion = slugify(spec.title);
          const idPlaceholder =
            titleSuggestion || t("editor.ai.insights.spec.idPlaceholder");
          return (
            <div
              key={idx}
              className="space-y-3 rounded-md border border-neutral-200 bg-neutral-50 p-4"
            >
              {/* Header: drag arrows + enabled toggle + remove */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                    className="rounded p-1 text-neutral-500 hover:bg-white hover:text-neutral-700 disabled:opacity-30"
                    aria-label="Move up"
                    title="Move up"
                  >
                    <ChevronUp size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => move(idx, 1)}
                    disabled={idx === specs.length - 1}
                    className="rounded p-1 text-neutral-500 hover:bg-white hover:text-neutral-700 disabled:opacity-30"
                    aria-label="Move down"
                    title="Move down"
                  >
                    <ChevronDown size={14} />
                  </button>
                  <span className="ml-2 text-xs font-medium text-neutral-500">
                    #{idx + 1}
                  </span>
                </div>
                <label className="flex cursor-pointer items-center gap-2 text-xs text-neutral-600">
                  <input
                    type="checkbox"
                    checked={spec.enabled !== false}
                    onChange={(e) =>
                      patch(idx, { enabled: e.target.checked })
                    }
                    className="h-4 w-4 rounded border-neutral-300 text-primary-dark focus:ring-primary"
                  />
                  {t("editor.ai.insights.spec.enabledLabel")}
                </label>
                <button
                  type="button"
                  onClick={() => remove(idx)}
                  className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-error-dark"
                >
                  <Trash2 size={12} />
                  {t("editor.ai.insights.removeSpec")}
                </button>
              </div>

              {/* ID field */}
              <Field
                label={t("editor.ai.insights.spec.idLabel")}
                hint={t("editor.ai.insights.spec.idHint")}
              >
                <input
                  type="text"
                  value={spec.id}
                  onChange={(e) =>
                    patch(idx, { id: e.target.value.toLowerCase() })
                  }
                  onFocus={() => {
                    // Auto-suggest a slug from the title only on first focus
                    // when the field is still empty.
                    if (!spec.id && titleSuggestion) {
                      patch(idx, { id: titleSuggestion });
                    }
                  }}
                  placeholder={idPlaceholder}
                  className="block w-full rounded-[10px] border border-control bg-white px-3 py-2 placeholder:text-neutral-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
                  pattern="[a-z0-9_]+"
                />
              </Field>

              {/* Title (bilingual) */}
              <Field
                label={t("editor.ai.insights.spec.titleLabel")}
                hint={t("editor.ai.insights.spec.titleHint")}
              >
                <BilingualInput
                  value={spec.title}
                  onChange={(next: LocalizedString) =>
                    patch(idx, { title: next })
                  }
                  placeholder="Crecimiento YoY de sellers NO"
                />
              </Field>

              {/* Question (bilingual, multi-line) */}
              <Field
                label={t("editor.ai.insights.spec.questionLabel")}
                hint={t("editor.ai.insights.spec.questionHint")}
              >
                <BilingualInput
                  value={spec.question}
                  onChange={(next: LocalizedString) =>
                    patch(idx, { question: next })
                  }
                  placeholder="¿Cómo cambió la cantidad de sellers NO vs hace 52 semanas?"
                  multiline
                />
              </Field>

              {/* Data focus chips */}
              <Field
                label={t("editor.ai.insights.spec.dataFocusLabel")}
                hint={t("editor.ai.insights.spec.dataFocusHint")}
              >
                <ChipsInput
                  value={spec.data_focus ?? []}
                  onChange={(next) => patch(idx, { data_focus: next })}
                  placeholder={t("editor.ai.insights.spec.dataFocusPlaceholder")}
                />
              </Field>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={add}
        className="mt-3 inline-flex items-center gap-1 rounded-md border border-dashed border-neutral-300 px-3 py-1.5 text-sm text-neutral-600 hover:bg-neutral-50"
      >
        + {t("editor.ai.insights.addSpec")}
      </button>
    </section>
  );
}
